<script lang="ts" setup>
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";

// Interface
interface Emit {
  (e: "closeDialog", value: boolean): void;
}

interface Props {
  isVisible?: boolean;
  docketLineId: string;
}

// Props
const props = defineProps<Props>();

// Emit
const emit = defineEmits<Emit>();

// Data
const docketLineId = ref<string>("");
const listHistory = ref<any[]>([]);
const headers = [
  { title: "Date", key: "created", sortable: true },
  { title: "POS", key: "posDevice", sortable: false },
  { title: "Server", key: "serverPerson", sortable: false },
  { title: "Desc", key: "notes", sortable: false },
  { title: "Amount", key: "qtyChange", sortable: false, width: '50px' },
  { title: "Type", key: "type", sortable: false, width: '50px', value: (item: any) => item.qtyChange > 0 ? 'Increase' : 'Decrease' },
  { title: "Balance", key: "balance", sortable: false, width: '50px' },
] as any[];
const options = ref<any>({
  page: 1,
  itemsPerPage: 10,
  sortBy: [],
  groupBy: [],
  search: undefined,
});
const isLoading = ref(false);
const totalList = ref<number>(0);

// Method
const fetchDocketLineHistory = async () => {
  try {
    if (isLoading.value) return;
    isLoading.value = true;

    let filter = `docketLine = "${docketLineId.value}"`;
    let sortParam = "-created";
    if (options.value?.sortBy.length) {
      sortParam =
        options.value?.sortBy[0].order === "desc"
          ? `-${options.value?.sortBy[0].key}`
          : options.value?.sortBy[0].key;
    }

    const records = await pb
      .collection("docketLineTransactions")
      .getList(options.value?.page, options.value?.itemsPerPage, {
        filter: filter,
        sort: sortParam,
        expand: "posDevice",
      });

    if (records) {
      listHistory.value = records?.items;
      totalList.value = records.totalItems || 0;
      options.value.page = records.page;
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const closeDialog = () => {
  emit("closeDialog", false);
};

// watch
watch(
  props,
  async (newValue, oldValue) => {
    if (newValue?.docketLineId && newValue?.isVisible) {
      docketLineId.value = newValue?.docketLineId;
      await fetchDocketLineHistory();
    }
  },
  { immediate: true }
);
</script>

<template>
  <VDialog
    persistent
    :model-value="props.isVisible"
    no-click-animation
    width="950"
    @update:is-drawer-open="closeDialog"
  >
    <VCard flat>
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <!-- 👉 Header -->
        <VCardTitle> Docket Line History </VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="closeDialog"
        ></VBtn>
      </div>
      <div>
        <VDataTableServer
          v-model:items-per-page="options.itemsPerPage"
          v-model:page="options.page"
          :loading="isLoading"
          :items-length="totalList"
          :headers="headers"
          :items="listHistory"
          class="text-no-wrap rounded-0"
          @update:options="
            ($event) => {
              options = $event;
              fetchDocketLineHistory();
            }
          "
        >
          <template #item.created="{ item }">
            <span class="text-sm">
              {{
                item.created
                  ? moment(item.created).format(moment(item.created).year() === moment().year() ? "DD MMM hh:mmA" : "DD MMM YYYY hh:mmA")
                  : ""
              }}
            </span>
          </template>

          <template #item.qtyChange="{ item }">
            <span
              class="text-sm font-weight-medium"
            >
              {{ item.qtyChange < 0 ? -item.qtyChange : item.qtyChange }}
            </span>
          </template>

          <template #item.type="{ item }">
            <span
              class="text-sm font-weight-medium"
              :class="item.qtyChange > 0 ? 'text-success' : 'text-error'"
            >
              {{ item.qtyChange > 0 ? 'Increase' : 'Decrease' }}
            </span>
          </template>

          <template #item.balance="{ item }">
            <span class="text-sm font-weight-medium">
              {{ item.balance ?? '-' }}
            </span>
          </template>

          <template #item.serverPerson="{ item }">
            <span class="text-sm"> {{ item.serverPerson || '-' }} </span>
          </template>

          <template #item.posDevice="{ item }">
            <span class="text-sm"> {{ item.expand?.posDevice?.name || '-' }} </span>
          </template>

          <!-- pagination  -->
          <template #bottom>
            <VDivider />
            <div class="d-flex gap-x-6 flex-wrap justify-end pa-2 align-center">
              <div class="text-sm ms-2">{{ totalList }} Records</div>
              <VSpacer />
              <div class="d-flex align-center gap-x-2 text-sm">
                Rows Per Page:
                <VSelect
                  v-model="options.itemsPerPage"
                  variant="plain"
                  class="per-page-select text-high-emphasis"
                  density="compact"
                  :items="[10, 20, 25, 50, 100]"
                  @update:modelValue="fetchDocketLineHistory()"
                />
              </div>
              <div class="d-flex text-sm align-center text-high-emphasis" />
              <div class="d-flex gap-x-2 align-center">
                <VBtn
                  class="flip-in-rtl"
                  icon="mdi-chevron-left"
                  variant="text"
                  density="comfortable"
                  color="default"
                  :disabled="options.page <= 1"
                  @click="
                    () => {
                      options.page <= 1 ? (options.page = 1) : options.page--,
                        fetchDocketLineHistory();
                    }
                  "
                />

                <VBtn
                  class="flip-in-rtl"
                  icon="mdi-chevron-right"
                  density="comfortable"
                  variant="text"
                  color="default"
                  :disabled="
                    options.page >= Math.ceil(totalList / options.itemsPerPage)
                  "
                  @click="
                    () => {
                      options.page >=
                      Math.ceil(totalList / options.itemsPerPage)
                        ? (options.page = Math.ceil(
                            totalList / options.itemsPerPage
                          ))
                        : options.page++,
                        fetchDocketLineHistory();
                    }
                  "
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>
  </VDialog>
</template>
